import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import api from '../utils/api';
import './AdminChat.css';

const getSocketUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace('/api', '');
  }
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5000';
    }
  }
  return window.location.origin;
};

const shortWallet = (addr) => addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : '';
const formatTime = (d) => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const formatDate = (d) => new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric' });

export default function AdminChat() {
  const [rooms, setRooms]               = useState([]); // { _id, lastMessage, lastAt, unread }
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages]         = useState([]);
  const [input, setInput]               = useState('');
  const [connected, setConnected]       = useState(false);
  const [loadError, setLoadError]       = useState(null);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  // Ref so socket handler always reads current selectedRoom without needing to reconnect
  const selectedRoomRef = useRef(null);

  useEffect(() => { selectedRoomRef.current = selectedRoom; }, [selectedRoom]);

  // Load room list
  const loadRooms = useCallback(async () => {
    try {
      setLoadError(null);
      // Pass wallet address header as fallback for sessions without a JWT token
      const walletAddress = window.ethereum?.selectedAddress || localStorage.getItem('walletAddress');
      const res = await api.get('/chat/admin/rooms', {
        headers: walletAddress ? { 'x-wallet-address': walletAddress } : {},
      });
      setRooms(res.data.rooms || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setLoadError('Not authorised. Make sure you are signed in as the admin wallet.');
      } else {
        setLoadError('Failed to load conversations. Retrying…');
      }
    }
  }, []);

  useEffect(() => { loadRooms(); }, [loadRooms]);

  // Fallback poll every 15 s so rooms appear even if socket admin_room assignment fails
  useEffect(() => {
    const id = setInterval(loadRooms, 15000);
    return () => clearInterval(id);
  }, [loadRooms]);

  // Connect socket
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = io(getSocketUrl(), {
      auth: { token },
      path: '/socket.io',
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect',    () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('chat:message', (msg) => {
      const curRoom = selectedRoomRef.current;

      // Append to open conversation — skip admin's own echoes (shown optimistically)
      if (curRoom && msg.roomId === curRoom && msg.fromAdmin === false) {
        setMessages(prev => {
          if (prev.some(m => String(m._id) === String(msg._id))) return prev;
          return [...prev, msg];
        });
      }

      // Always update sidebar (new room / unread count / last message preview)
      setRooms(prev => {
        const exists = prev.find(r => r._id === msg.roomId);
        const updated = {
          _id: msg.roomId,
          lastMessage: msg.message,
          lastAt: msg.createdAt,
          lastFromAdmin: msg.fromAdmin,
          unread: exists ? (msg.fromAdmin ? exists.unread : exists.unread + 1) : (msg.fromAdmin ? 0 : 1),
        };
        if (exists) {
          return prev.map(r => r._id === msg.roomId ? { ...r, ...updated } : r)
                     .sort((a, b) => new Date(b.lastAt) - new Date(a.lastAt));
        }
        return [updated, ...prev];
      });
    });

    return () => { socket.disconnect(); };
  }, []); // ← empty: connect once, ref tracks selectedRoom

  // Load messages for selected room
  const openRoom = useCallback(async (roomId) => {
    setSelectedRoom(roomId);
    setMessages([]);
    const walletAddress = window.ethereum?.selectedAddress || localStorage.getItem('walletAddress');
    const headers = walletAddress ? { 'x-wallet-address': walletAddress } : {};
    try {
      const res = await api.get(`/chat/admin/room/${roomId}`, { headers });
      setMessages(res.data.messages || []);
      await api.post(`/chat/admin/read/${roomId}`, {}, { headers });
      setRooms(prev => prev.map(r => r._id === roomId ? { ...r, unread: 0 } : r));
      if (socketRef.current?.connected) {
        socketRef.current.emit('chat:read', { roomId });
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || !socketRef.current?.connected || !selectedRoom) return;

    // Show optimistically
    const tempMsg = {
      _id: 'temp_' + Date.now(),
      fromAdmin: true,
      message: trimmed,
      roomId: selectedRoom,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempMsg]);
    setInput('');

    socketRef.current.emit('chat:send', { message: trimmed, targetRoom: selectedRoom });
  }, [input, selectedRoom]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const totalUnread = rooms.reduce((s, r) => s + (r.unread || 0), 0);

  // Group messages by date
  const grouped = [];
  let lastDate = null;
  messages.forEach(msg => {
    const d = formatDate(msg.createdAt);
    if (d !== lastDate) { grouped.push({ type: 'date', label: d }); lastDate = d; }
    grouped.push({ type: 'msg', msg });
  });

  return (
    <div className="ac-root">
      {/* Sidebar: room list */}
      <div className="ac-sidebar">
        <div className="ac-sidebar-header">
          <h3>Support Inbox {totalUnread > 0 && <span className="ac-badge">{totalUnread}</span>}</h3>
          <button className="ac-refresh-btn" onClick={loadRooms} title="Refresh">↻</button>
        </div>
        <div className="ac-room-list">
          {loadError && <p className="ac-empty-rooms" style={{color:'#e05c5c'}}>{loadError}</p>}
          {!loadError && rooms.length === 0 && <p className="ac-empty-rooms">No conversations yet.</p>}
          {rooms.map(room => (
            <button
              key={room._id}
              className={`ac-room-item ${selectedRoom === room._id ? 'ac-room-active' : ''}`}
              onClick={() => openRoom(room._id)}
            >
              <div className="ac-room-top">
                <span className="ac-room-wallet">{shortWallet(room._id)}</span>
                {room.unread > 0 && <span className="ac-badge">{room.unread}</span>}
              </div>
              <p className="ac-room-preview">
                {room.lastFromAdmin ? '(You) ' : ''}{room.lastMessage}
              </p>
              <span className="ac-room-time">{formatDate(room.lastAt)} {formatTime(room.lastAt)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Chat panel */}
      <div className="ac-chat">
        {!selectedRoom ? (
          <div className="ac-no-room">
            <p>Select a conversation to start replying.</p>
          </div>
        ) : (
          <>
            <div className="ac-chat-header">
              <span className="ac-chat-wallet">{selectedRoom}</span>
              <span className={`ac-conn-dot ${connected ? 'ac-conn-on' : ''}`} title={connected ? 'Connected' : 'Disconnected'} />
            </div>

            <div className="ac-messages">
              {grouped.map((item, i) =>
                item.type === 'date'
                  ? <div key={`d-${i}`} className="ac-date-divider">{item.label}</div>
                  : (
                    <div
                      key={item.msg._id || i}
                      className={`ac-bubble-row ${item.msg.fromAdmin ? 'ac-row-admin' : 'ac-row-user'}`}
                    >
                      <div className={`ac-bubble ${item.msg.fromAdmin ? 'ac-bubble-admin' : 'ac-bubble-user'}`}>
                        <p className="ac-bubble-text">{item.msg.message}</p>
                        <span className="ac-bubble-time">{formatTime(item.msg.createdAt)}</span>
                      </div>
                    </div>
                  )
              )}
              <div ref={bottomRef} />
            </div>

            <div className="ac-input-row">
              <textarea
                className="ac-input"
                placeholder="Reply…"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                rows={1}
                maxLength={2000}
              />
              <button
                className="ac-send-btn"
                onClick={send}
                disabled={!input.trim() || !connected || !selectedRoom}
              >
                Send
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
